import { create } from 'zustand'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

interface AuthState {
  user: User | null
  session: Session | null
  isLoading: boolean
  isInitialized: boolean
  error: string | null

  // Actions
  initialize: () => Promise<void>
  signIn: (email: string, password: string, captchaToken?: string) => Promise<{ error: string | null }>
  signUp: (email: string, password: string, role?: 'user' | 'supporter', captchaToken?: string) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  session: null,
  isLoading: false,
  isInitialized: false,
  error: null,

  initialize: async () => {
    try {
      // Get current session
      const { data: { session }, error } = await supabase.auth.getSession()

      if (error) {
        set({ error: error.message, isLoading: false, isInitialized: true })
        return
      }

      set({
        session,
        user: session?.user ?? null,
        isLoading: false,
        isInitialized: true,
      })

      // Set up auth state change listener
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        set({
          session,
          user: session?.user ?? null,
        })
      })
      // Store for potential cleanup
      void subscription
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Failed to initialize auth',
        isLoading: false,
        isInitialized: true,
      })
    }
  },

  signIn: async (email, password, captchaToken?: string) => {
    set({ isLoading: true, error: null })

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: captchaToken ? { captchaToken } : undefined,
      })

      if (error) {
        set({ error: error.message, isLoading: false })
        return { error: error.message }
      }

      set({
        session: data.session,
        user: data.user,
        isLoading: false,
      })

      return { error: null }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Log in failed'
      set({ error: errorMessage, isLoading: false })
      return { error: errorMessage }
    }
  },

  signUp: async (email, password, role = 'user', captchaToken?: string) => {
    set({ isLoading: true, error: null })

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { role },
          captchaToken,
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      })

      // DIAG: confirms which role the signup wrote. role='supporter' must
      // match the renamed app_role enum value (migration 108) so the
      // handle_new_supporter trigger fires.
      console.info('[DWF-DIAG] authStore.signUp role=', role, 'error=', error?.message ?? null)

      if (error) {
        set({ error: error.message, isLoading: false })
        return { error: error.message }
      }

      // If email confirmation is required, user will be null but no error
      if (data.user && !data.session) {
        set({ isLoading: false })
        return { error: null } // Email confirmation required
      }

      set({
        session: data.session,
        user: data.user,
        isLoading: false,
      })

      return { error: null }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Account creation failed'
      set({ error: errorMessage, isLoading: false })
      return { error: errorMessage }
    }
  },

  signOut: async () => {
    set({ isLoading: true })

    try {
      await supabase.auth.signOut()
      set({
        user: null,
        session: null,
        isLoading: false,
        error: null,
      })
    } catch (err) {
      set({
        error: err instanceof Error ? err.message : 'Sign out failed',
        isLoading: false,
      })
    }
  },

  clearError: () => set({ error: null }),
}))
